import { useEffect, useState, useRef } from "react";
import {
  adminListCollection, adminCreateCollection, adminUpdateCollection, adminDeleteCollection, adminReorder, adminToggleVisibility, adminTaxonomyImport,
  fetchAdminSettings, adminUpdateSettings,
} from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploader from "@/components/ImageUploader";
import EntryPreview from "@/components/admin/EntryPreview";
import CollectionTranslations from "@/components/CollectionTranslations";
import { Plus, Pencil, Trash2, X, Save, GripVertical, Eye, EyeOff, DownloadCloud, Loader2, FolderPlus, ExternalLink } from "lucide-react";
import { NAV } from "@/lib/nav";
import { toast } from "sonner";

const COLLECTIONS = [
  { key: "hub", label: "Knowledge Hub", name: (d) => d.title, sub: (d) => `${d.section} · ${d.slug}` },
  { key: "whatsnew", label: "What's New (Custom)", name: (d) => d.title, sub: (d) => d.body?.slice(0, 60) },
  { key: "categories", label: "Categories", name: (d) => d.name, sub: (d) => d.slug },
  { key: "deities", label: "Deities", name: (d) => d.name, sub: (d) => d.epithet || d.slug },
  { key: "temples", label: "Temples", name: (d) => d.name, sub: (d) => [d.location, d.state].filter(Boolean).join(", ") },
  { key: "darshan", label: "Live Darshan", name: (d) => d.name, sub: (d) => `${d.location || ""}${d.channel_id ? " · inline embed" : " · link only"}` },
  { key: "festivals", label: "Festival Calendar", name: (d) => d.name, sub: (d) => `${d.date || ""} · ${d.type || ""}` },
  { key: "japa_mantras", label: "Japa Mantras", name: (d) => d.deva, sub: (d) => `${d.translit || ""} · ${d.deity || ""}` },
  { key: "quiz", label: "Dharma Quiz", name: (d) => d.question, sub: (d) => `${d.category} · answer #${(d.answer ?? 0) + 1}` },
];

const SCHEMAS = {
  whatsnew: [
    { k: "title", t: "text", label: "Title", req: true },
    { k: "body", t: "textarea", label: "Description" },
    { k: "link", t: "text", label: "Link (optional)" },
    { k: "image", t: "image", label: "Image" },
  ],
  categories: [
    { k: "name", t: "text", label: "Name (English)", req: true },
    { k: "deva_name", t: "text", label: "Devanagari name", deva: true },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "description", t: "textarea", label: "Description (English)" },
    { k: "description_hi", t: "textarea", label: "Description (Hindi)", deva: true },
    { k: "image", t: "image", label: "Image" },
  ],
  hub: [
    { k: "section", t: "select", label: "Section", opts: ["scriptures", "rishis", "concepts", "pilgrimage", "samskara", "sanskrit"] },
    { k: "title", t: "text", label: "Title (English)", req: true },
    { k: "title_hi", t: "text", label: "Title (Hindi)", deva: true },
    { k: "deva_name", t: "text", label: "Devanagari name", deva: true },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "subtitle", t: "text", label: "Subtitle (English)" },
    { k: "subtitle_hi", t: "text", label: "Subtitle (Hindi)", deva: true },
    { k: "detail_heading", t: "text", label: "Intro heading (English)" },
    { k: "detail_heading_hi", t: "text", label: "Intro heading (Hindi)", deva: true },
    { k: "summary", t: "rich", label: "Summary / explainer (English)" },
    { k: "summary_hi", t: "rich", label: "Summary / explainer (Hindi)" },
    { k: "body", t: "rich", label: "Body (English)" },
    { k: "body_hi", t: "rich", label: "Body (Hindi)" },
    { k: "points", t: "objlist", label: "Key points (English)" },
    { k: "points_hi", t: "objlist", label: "Key points (Hindi)" },
    { k: "related", t: "tags", label: "Related content slugs" },
    { k: "image", t: "image", label: "Image" },
  ],
  deities: [
    { k: "name", t: "text", label: "Name", req: true },
    { k: "deva_name", t: "text", label: "Devanagari name", deva: true },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "category", t: "select", label: "Category", opts: [] },
    { k: "epithet", t: "text", label: "Epithet (English)" },
    { k: "epithet_hi", t: "text", label: "Epithet (Hindi)", deva: true },
    { k: "domain", t: "text", label: "Domain" },
    { k: "aliases", t: "tags", label: "Aliases (for linking content)" },
    { k: "description", t: "rich", label: "Description (English)" },
    { k: "description_hi", t: "rich", label: "Description (Hindi)" },
    { k: "significance", t: "rich", label: "Significance (English)" },
    { k: "significance_hi", t: "rich", label: "Significance (Hindi)" },
    { k: "image", t: "image", label: "Image" },
  ],
  temples: [
    { k: "name", t: "text", label: "Name", req: true },
    { k: "deva_name", t: "text", label: "Devanagari name", deva: true },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "category", t: "select", label: "Category", opts: [] },
    { k: "deity", t: "text", label: "Primary deity" },
    { k: "location", t: "text", label: "Location (city)" },
    { k: "state", t: "text", label: "State" },
    { k: "region", t: "select", label: "Region", opts: ["North", "South", "East", "West", "Central", "Northeast"] },
    { k: "era", t: "text", label: "Era / period" },
    { k: "image", t: "image", label: "Image" },
    { k: "website", t: "text", label: "Official website" },
    { k: "booking_url", t: "text", label: "Pooja/Seva booking URL" },
    { k: "timings", t: "text", label: "Timings" },
    { k: "description", t: "rich", label: "Description (English)" },
    { k: "description_hi", t: "rich", label: "Description (Hindi)" },
    { k: "significance", t: "rich", label: "Significance (English)" },
    { k: "significance_hi", t: "rich", label: "Significance (Hindi)" },
    { k: "highlights", t: "strlist", label: "Highlights" },
    { k: "festivals", t: "strlist", label: "Festivals" },
  ],
  quiz: [
    { k: "question", t: "textarea", label: "Question", req: true },
    { k: "options", t: "strlist", label: "Options" },
    { k: "answer", t: "number", label: "Correct option number (0 = first)" },
    { k: "explanation", t: "rich", label: "Explanation" },
    { k: "category", t: "text", label: "Category" },
  ],
  jyotirlingas: [
    { k: "en", t: "text", label: "Name (English)", req: true },
    { k: "deva", t: "text", label: "Devanagari name" },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "location", t: "text", label: "Location (city)" },
    { k: "state", t: "text", label: "State" },
    { k: "temple_slug", t: "text", label: "Linked temple slug (e.g. somnath)" },
    { k: "image_url", t: "image", label: "Image" },
    { k: "body_en", t: "rich", label: "Significance (English)" },
    { k: "body_hi", t: "rich", label: "Significance (Hindi)" },
  ],
  shakti_peethas: [
    { k: "en", t: "text", label: "Name (English)", req: true },
    { k: "deva", t: "text", label: "Devanagari name" },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "location", t: "text", label: "Location" },
    { k: "part", t: "text", label: "Relic / body part" },
    { k: "temple_slug", t: "text", label: "Linked temple slug (e.g. kamakhya)" },
    { k: "image_url", t: "image", label: "Image" },
    { k: "body_en", t: "rich", label: "Significance (English)" },
    { k: "body_hi", t: "rich", label: "Significance (Hindi)" },
  ],
  darshan: [
    { k: "name", t: "text", label: "Temple / channel name", req: true },
    { k: "deva", t: "text", label: "Devanagari name" },
    { k: "slug", t: "text", label: "Slug (unique URL id)", req: true },
    { k: "location", t: "text", label: "Location (city)" },
    { k: "channel_id", t: "text", label: "YouTube Channel ID (UC…) — enables the INLINE live player" },
    { k: "youtube", t: "text", label: "YouTube link (channel/watch URL) — used as fallback 'Watch live'", req: true },
    { k: "order", t: "number", label: "Display order (lower shows first)" },
  ],
  festivals: [
    { k: "date", t: "text", label: "Date (YYYY-MM-DD)", req: true },
    { k: "name", t: "text", label: "Festival / observance name (English)", req: true },
    { k: "name_hi", t: "text", label: "Festival / observance name (Hindi)", deva: true },
    { k: "deva", t: "text", label: "Devanagari name", deva: true },
    { k: "type", t: "select", label: "Type", opts: ["festival", "vrata", "ekadashi", "jayanti", "sankranti"] },
    { k: "significance", t: "rich", label: "Significance (English)" },
    { k: "significance_hi", t: "rich", label: "Significance (Hindi)" },
  ],
  japa_mantras: [
    { k: "deva", t: "text", label: "Mantra (Devanagari)", req: true },
    { k: "translit", t: "text", label: "Transliteration", req: true },
    { k: "mid", t: "text", label: "Unique id (slug, e.g. om-namah-shivaya)", req: true },
    { k: "deity", t: "text", label: "Deity" },
    { k: "meaning", t: "textarea", label: "Meaning" },
    { k: "order", t: "number", label: "Display order (lower shows first)" },
  ],
};

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

function Field({ f, value, onChange }) {
  const label = <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{f.label}{f.req && <span className="text-saffron"> *</span>}</label>;
  if (f.t === "text" || f.t === "number")
    return <div>{label}<input type={f.t === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(f.t === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} className={`${inp} ${f.deva ? "font-deva-body" : ""}`} /></div>;
  if (f.t === "textarea")
    return <div>{label}<textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${inp} ${f.deva ? "font-deva-body" : ""}`} /></div>;
  if (f.t === "select")
    return <div>{label}<select value={value ?? f.opts[0]} onChange={(e) => onChange(e.target.value)} className={inp}>{f.opts.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
  if (f.t === "rich")
    return <div>{label}<RichTextEditor value={value || ""} onChange={onChange} /></div>;
  if (f.t === "image")
    return <ImageUploader label={f.label} value={value} onChange={onChange} testid={`col-${f.k}`} />;
  if (f.t === "tags")
    return <div>{label}<input value={Array.isArray(value) ? value.join(", ") : (value || "")} onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="comma-separated" className={inp} /></div>;
  if (f.t === "strlist") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>{label}
        <div className="space-y-2">
          {arr.map((v, i) => (
            <div key={i} className="flex gap-2">
              <input value={v} onChange={(e) => onChange(arr.map((x, idx) => idx === i ? e.target.value : x))} className={inp} />
              <button type="button" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="px-2 text-ink-3 hover:text-[var(--saffron-dark)]"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => onChange([...arr, ""])} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-[var(--ink)] hover:text-ink"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
      </div>
    );
  }
  if (f.t === "objlist") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>{label}
        <div className="space-y-2">
          {arr.map((v, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] gap-2">
              <input placeholder="Heading" value={v.head || ""} onChange={(e) => onChange(arr.map((x, idx) => idx === i ? { ...x, head: e.target.value } : x))} className={inp} />
              <input placeholder="Text" value={v.text || ""} onChange={(e) => onChange(arr.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x))} className={inp} />
              <button type="button" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="px-2 text-ink-3 hover:text-[var(--saffron-dark)]"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => onChange([...arr, { head: "", text: "" }])} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 hover:border-[var(--ink)] hover:text-ink"><Plus className="h-3.5 w-3.5" /> Add point</button>
        </div>
      </div>
    );
  }
  return null;
}

export default function AdminCollections({ only = null, showImport = false }) {
  const visibleCollections = only ? COLLECTIONS.filter((c) => only.includes(c.key)) : COLLECTIONS;
  const [active, setActive] = useState(visibleCollections[0]?.key || "hub");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const cfg = COLLECTIONS.find((c) => c.key === active);
  const schema = SCHEMAS[active];

  const load = () => { setLoading(true); adminListCollection(active).then(setItems).catch(() => setItems([])).finally(() => setLoading(false)); };
  useEffect(load, [active]);

  // ── Knowledge Hub sections (create / rename + drag-to-recategorise) ──
  const HUB_DEFAULT_SECTIONS = [
    { key: "scriptures", label: "Scriptures" }, { key: "rishis", label: "Rishis" },
    { key: "concepts", label: "Concepts" }, { key: "pilgrimage", label: "Pilgrimage" },
    { key: "samskara", label: "Samskara" }, { key: "sanskrit", label: "Sanskrit" },
  ];
  const [hubSections, setHubSections] = useState(null);
  useEffect(() => {
    fetchAdminSettings().then((s) => {
      const saved = Array.isArray(s.hub_sections) ? s.hub_sections.filter((x) => x && x.key) : [];
      const ordered = saved.map((x) => ({ key: x.key, label: x.label || x.key }));
      HUB_DEFAULT_SECTIONS.forEach((d) => { if (!ordered.find((o) => o.key === d.key)) ordered.push(d); });
      setHubSections(ordered);
    }).catch(() => setHubSections(HUB_DEFAULT_SECTIONS));
  }, []);
  const saveHubSections = async (next) => { setHubSections(next); try { await adminUpdateSettings({ hub_sections: next }); } catch (e) { toast.error("Could not save sections"); } };
  const setHubSectionGroup = (key, group) => saveHubSections((hubSections || []).map((s) => s.key === key ? { ...s, group } : s));

  // ── Category groups for Deities / Temples (create / rename / delete + drag-to-regroup) ──
  const CAT_SETTING = { deities: "deity_categories", temples: "temple_categories" };
  const [catGroups, setCatGroups] = useState({ deities: [], temples: [] });
  useEffect(() => {
    fetchAdminSettings().then((s) => setCatGroups({
      deities: (Array.isArray(s.deity_categories) ? s.deity_categories : []).filter((x) => x && x.key),
      temples: (Array.isArray(s.temple_categories) ? s.temple_categories : []).filter((x) => x && x.key),
    })).catch(() => {});
  }, []);

  // Unified board config: hub groups by `section`, deities/temples by `category`.
  const isBoard = active === "hub" ? true : !!CAT_SETTING[active];
  const groupField = active === "hub" ? "section" : "category";
  const boardGroups = active === "hub" ? (hubSections || []) : (catGroups[active] || []);
  const boardDefaults = active === "hub" ? HUB_DEFAULT_SECTIONS : [];

  const saveBoardGroups = async (next) => {
    if (active === "hub") { saveHubSections(next); return; }
    setCatGroups((p) => ({ ...p, [active]: next }));
    try { await adminUpdateSettings({ [CAT_SETTING[active]]: next }); } catch (e) { toast.error("Could not save categories"); }
  };
  const addBoardGroup = () => {
    const label = window.prompt(active === "hub" ? "New section name (e.g. Vedangas)" : "New category name (e.g. Shaiva)"); if (!label) return;
    const key = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); if (!key) return;
    if (boardGroups.find((s) => s.key === key)) { toast("That already exists"); return; }
    saveBoardGroups([...boardGroups, active === "hub" ? { key, label: label.trim(), group: "explore" } : { key, label: label.trim() }]);
    toast(active === "hub" ? "Section added" : "Category added");
  };
  const renameBoardGroup = (key) => {
    const cur = boardGroups.find((s) => s.key === key); const label = window.prompt("Rename", cur?.label || key);
    if (label == null) return; saveBoardGroups(boardGroups.map((s) => s.key === key ? { ...s, label: label.trim() || key } : s));
  };
  const deleteBoardGroup = (key) => {
    if (!window.confirm(`Delete this ${active === "hub" ? "section" : "category"}? Entries inside it are kept and move to "Unassigned".`)) return;
    saveBoardGroups(boardGroups.filter((s) => s.key !== key)); toast("Removed");
  };

  // ── Drag & drop reorder / recategorise ──
  const drag = useRef(null);
  const [overKey, setOverKey] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [hubFilter, setHubFilter] = useState("all");
  const clearDrag = () => { drag.current = null; setOverKey(null); };
  const persistOrder = (arr) => { setItems(arr); adminReorder(active, arr.map((x) => x.id)).catch(() => { toast.error("Could not save order"); load(); }); };
  const onFlatDrop = (targetId) => {
    const d = drag.current; clearDrag(); if (!d || d.type !== "row" || d.id === targetId) return;
    const arr = [...items]; const from = arr.findIndex((x) => x.id === d.id); const to = arr.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); persistOrder(arr);
  };
  const hubDrop = async (secKey, beforeId) => {
    const d = drag.current; clearDrag(); if (!d || d.type !== "row") return;
    const moving = items.find((x) => x.id === d.id); if (!moving) return;
    if ((moving[groupField] || "") === secKey && d.id === beforeId) return;
    const changing = (moving[groupField] || "") !== secKey;
    let arr = items.filter((x) => x.id !== d.id);
    const updated = { ...moving, [groupField]: secKey };
    let idx;
    if (beforeId) { idx = arr.findIndex((x) => x.id === beforeId); if (idx < 0) idx = arr.length; }
    else { const last = arr.map((x) => x[groupField]).lastIndexOf(secKey); idx = last >= 0 ? last + 1 : arr.length; }
    arr.splice(idx, 0, updated); setItems(arr);
    try {
      if (changing) { const { id, created_at, qid, ...body } = updated; await adminUpdateCollection(active, moving.id, body); }
      await adminReorder(active, arr.map((x) => x.id));
      if (changing) toast("Recategorised");
    } catch (e) { toast.error("Could not save"); load(); }
  };

  const runImport = async () => {
    if (!window.confirm("Scan all content and turn existing Category / Deity values into managed taxonomy entries? This is safe to run multiple times.")) return;
    setImporting(true);
    try {
      const r = await adminTaxonomyImport();
      toast.success("Import complete", { description: `${r.categories_created} categories, ${r.deities_created} deities created · ${r.content_updated} items mapped` });
      load();
    } catch (err) {
      toast.error("Import failed", { description: err.response?.data?.detail || err.message });
    } finally { setImporting(false); }
  };

  const openNew = () => setEditing({});
  const openEdit = (it) => { const { created_at, qid, ...rest } = it; setEditing(rest); };

  const save = async () => {
    for (const f of schema) if (f.req && !editing[f.k]) { toast.error(`${f.label} is required.`); return; }
    const data = { ...editing };
    if (active === "quiz") {
      const opts = Array.isArray(data.options) ? data.options.filter((o) => (o || "").trim()) : [];
      data.options = opts;
      const a = Number(data.answer) || 0;
      data.answer = Math.max(0, Math.min(a, Math.max(0, opts.length - 1)));
    }
    const { id, ...body } = data;
    setSaving(true);
    try {
      if (id) await adminUpdateCollection(active, id, body);
      else await adminCreateCollection(active, body);
      toast(id ? "Entry updated" : "Entry created");
      setEditing(null); load();
    } catch (err) { toast.error("Save failed", { description: err.response?.data?.detail || err.message }); }
    finally { setSaving(false); }
  };

  const remove = async (it) => { if (!window.confirm(`Delete "${cfg.name(it) || "this entry"}"?`)) return; await adminDeleteCollection(active, it.id); toast("Deleted"); load(); };
  const toggle = async (it) => { const r = await adminToggleVisibility(active, it.id); setItems((p) => p.map((x) => x.id === it.id ? { ...x, hidden: r.hidden } : x)); toast(r.hidden ? "Hidden" : "Visible"); };

  const effSchema = isBoard
    ? (schema || []).map((f) => (f.k === groupField ? { ...f, opts: boardGroups.map((s) => s.key) } : f))
    : schema;

  const rowActions = (it) => (
    <div className="flex items-center justify-end gap-1">
      <button data-testid={`collection-toggle-${it.id}`} onClick={() => toggle(it)} title={it.hidden ? "Show" : "Hide"} className="p-2 text-ink-2 hover:text-saffron">{it.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
      <button data-testid={`collection-edit-${it.id}`} onClick={() => openEdit(it)} className="p-2 text-ink-2 hover:text-saffron"><Pencil className="h-4 w-4" /></button>
      <button data-testid={`collection-delete-${it.id}`} onClick={() => remove(it)} className="p-2 text-ink-2 hover:text-[var(--saffron-dark)]"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
  const dragRow = (it, onDropRow) => (
    <div key={it.id} draggable onDragStart={() => { drag.current = { type: "row", id: it.id }; }} onDragEnd={clearDrag}
      onDragOver={(e) => { if (drag.current?.type === "row") { e.preventDefault(); e.stopPropagation(); setOverKey(it.id); } }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropRow(it.id); }}
      data-testid={`collection-row-${it.id}`}
      className={`mb-1.5 flex items-center gap-3 border bg-parchment px-2.5 py-2 transition-colors ${overKey === it.id ? "border-saffron ring-1 ring-saffron/40" : "border-line"}`}>
      <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-ink-3 active:cursor-grabbing" />
      <div className="min-w-0 flex-1">
        <span className={`font-deva-body ${it.hidden ? "text-ink-3 line-through" : "text-ink"}`}>{cfg.name(it) || "(untitled)"}</span>
        {it.deva_name && <span className="ml-2 font-deva text-ink-3">{it.deva_name}</span>}
        {it.hidden && <span className="ml-2 border border-line px-1.5 text-[10px] uppercase text-ink-3">Hidden</span>}
        <span className="ml-2 hidden text-xs text-ink-2 sm:inline">{cfg.sub(it)}</span>
      </div>
      {rowActions(it)}
    </div>
  );

  if (editing) {
    const PUBLIC_PATH = { hub: "/learn", deities: "/deities", temples: "/temples", categories: "/categories" };
    const publicUrl = (editing.id && editing.slug && PUBLIC_PATH[active]) ? `${PUBLIC_PATH[active]}/${editing.slug}` : null;
    return (
      <div className="flex flex-col gap-8 lg:flex-row">
        <div className={showPreview ? "min-w-0 lg:w-1/2" : "w-full max-w-3xl"}>
          <div className="mb-4 flex items-center justify-between">
            <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron"><X className="h-4 w-4" /> Cancel</button>
            <div className="flex items-center gap-4">
              {publicUrl && (
                <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="collection-view-live" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron">
                  <ExternalLink className="h-4 w-4" /> View on site
                </a>
              )}
              <button data-testid="preview-toggle" onClick={() => setShowPreview((s) => !s)} className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron">
                <Eye className="h-4 w-4" /> {showPreview ? "Hide preview" : "Live preview"}
              </button>
            </div>
          </div>
          <h1 className="mb-6 font-serif-latin text-3xl text-ink">{editing.id ? "Edit" : "New"} · {cfg.label}</h1>
          <div className="space-y-4">
            {effSchema.map((f) => (
              <Field key={f.k} f={f} value={editing[f.k]} onChange={(v) => setEditing((e) => ({ ...e, [f.k]: v }))} />
            ))}
            {editing.id && ["hub", "deities", "temples"].includes(active) && (
              <CollectionTranslations name={active} itemId={editing.id} i18n={editing.i18n || {}} onChange={(v) => setEditing((e) => ({ ...e, i18n: v }))} />
            )}
            <button data-testid="collection-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)] disabled:opacity-60"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
        {showPreview && (
          <div className="min-w-0 lg:w-1/2" data-testid="preview-panel">
            <div className="lg:sticky lg:top-6">
              <p className="overline-label mb-3 text-ink-3">Live preview · how the public page will look</p>
              <div className="max-h-[82vh] overflow-y-auto border border-line bg-[var(--surface)] p-6 md:p-8">
                <EntryPreview schema={effSchema} data={editing} />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">{only ? "Taxonomy" : "Sections"}</h1>
          <p className="mt-1 text-sm text-ink-3">{only ? "Define Categories, Deities & Temples, then map them to content from the editor." : "Add, edit, reorder and hide entries — no code required."}</p>
        </div>
        {showImport && (
          <button data-testid="taxonomy-import" onClick={runImport} disabled={importing} className="inline-flex items-center gap-2 border border-saffron px-4 py-2 text-sm font-medium text-saffron transition-colors hover:bg-[rgba(199,80,0,0.06)] disabled:opacity-60">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />} Import from content
          </button>
        )}
      </div>
      <div className="mb-8 flex flex-wrap gap-2 border-b border-line pb-4">
        {visibleCollections.map((c) => (
          <button key={c.key} data-testid={`collection-tab-${c.key}`} onClick={() => setActive(c.key)} className={`px-4 py-2 text-sm transition-colors ${active === c.key ? "bg-[var(--saffron)] text-white" : "border border-line text-ink-2 hover:border-[var(--ink)] hover:text-ink"}`}>{c.label}</button>
        ))}
      </div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink-3">{loading ? "…" : `${items.length} entries`}</p>
          {isBoard && (
            <label className="flex items-center gap-2 text-xs text-ink-3">
              <span className="uppercase tracking-wider">{active === "hub" ? "Filter section" : "Filter category"}</span>
              <select data-testid="hub-section-filter" value={hubFilter} onChange={(e) => setHubFilter(e.target.value)}
                className="border border-line bg-surface px-2.5 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none">
                <option value="all">{active === "hub" ? "All sections" : "All categories"}</option>
                {boardGroups.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isBoard && (
            <button data-testid="hub-add-section" onClick={addBoardGroup} className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron"><FolderPlus className="h-4 w-4" /> {active === "hub" ? "Add section" : "Add category"}</button>
          )}
          <button data-testid="collection-new" onClick={openNew} className="inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]"><Plus className="h-4 w-4" /> New Entry</button>
        </div>
      </div>
      {isBoard && <p className="mb-3 text-xs text-ink-3">Drag the <GripVertical className="inline h-3.5 w-3.5" /> handle to reorder within a {active === "hub" ? "section" : "category"}, or drop an entry into another {active === "hub" ? "section" : "category"} to regroup it.</p>}
      {loading ? <p className="py-16 text-center text-ink-3">…</p> : isBoard ? (
        <div className="space-y-4" data-testid="hub-board">
          {boardGroups.filter((sec) => hubFilter === "all" || sec.key === hubFilter).map((sec) => {
            const secItems = items.filter((it) => (it[groupField] || "") === sec.key);
            const on = overKey === `sec:${sec.key}`;
            return (
              <div key={sec.key} data-testid={`hub-section-${sec.key}`}
                onDragOver={(e) => { if (drag.current?.type === "row") { e.preventDefault(); setOverKey(`sec:${sec.key}`); } }}
                onDrop={(e) => { e.preventDefault(); hubDrop(sec.key, null); }}
                className={`border bg-surface transition-colors ${on ? "border-saffron ring-1 ring-saffron/40" : "border-line"}`}>
                <div className="flex items-center gap-2 border-b border-line bg-surface-alt/40 px-3 py-2">
                  <h3 className="font-serif-latin text-lg text-ink">{sec.label}</h3>
                  <code className="text-[11px] text-ink-3">{sec.key}</code>
                  <span className="text-xs text-ink-3">· {secItems.length}</span>
                  <div className="ml-auto flex items-center gap-2">
                    {active === "hub" && !boardDefaults.some((x) => x.key === sec.key) && (
                      <label className="flex items-center gap-1 text-xs text-ink-3">Menu group
                        <select data-testid={`hub-section-group-${sec.key}`} value={sec.group || "explore"} onChange={(e) => setHubSectionGroup(sec.key, e.target.value)}
                          className="border border-line bg-surface px-1.5 py-1 text-xs text-ink focus:border-saffron focus:outline-none">
                          {NAV.map((g) => <option key={g.key} value={g.key}>{g.label.en}</option>)}
                        </select>
                      </label>
                    )}
                    <button data-testid={`hub-section-rename-${sec.key}`} onClick={() => renameBoardGroup(sec.key)} className="text-xs text-ink-3 hover:text-saffron">Rename</button>
                    {!boardDefaults.some((x) => x.key === sec.key) && (
                      <button data-testid={`hub-section-delete-${sec.key}`} onClick={() => deleteBoardGroup(sec.key)} className="text-xs text-ink-3 hover:text-[var(--saffron-dark)]">Delete</button>
                    )}
                  </div>
                </div>
                <div className="p-2">
                  {secItems.map((it) => dragRow(it, (bid) => hubDrop(sec.key, bid)))}
                  {secItems.length === 0 && <p className="px-2 py-4 text-xs text-ink-3">Drop entries here.</p>}
                </div>
              </div>
            );
          })}
          {(() => {
            const known = new Set(boardGroups.map((s) => s.key));
            const others = items.filter((it) => !known.has(it[groupField] || ""));
            if (!others.length || hubFilter !== "all") return null;
            return (
              <div className="border border-dashed border-line bg-surface" data-testid="hub-section-other">
                <div className="flex items-center gap-2 border-b border-line bg-surface-alt/40 px-3 py-2">
                  <h3 className="font-serif-latin text-lg text-ink">Unassigned / Other</h3>
                  <span className="text-xs text-ink-3">· {others.length}</span>
                </div>
                <div className="p-2">{others.map((it) => dragRow(it, onFlatDrop))}</div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="overflow-hidden border border-line bg-surface">
          <div className="p-2" data-testid="collection-list">
            {items.map((it) => dragRow(it, onFlatDrop))}
            {items.length === 0 && <p className="px-4 py-16 text-center text-ink-3">No entries yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
